import { useState, useEffect, useCallback } from 'react';
import { CONFIG } from '../config';

export type NotificationPermission = 'default' | 'granted' | 'denied';

interface UseNotificationPermissionReturn {
  permission: NotificationPermission;
  isSupported: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  isLoading: boolean;
  error: string | null;
}

export const useNotificationPermission = (): UseNotificationPermissionReturn => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Проверяем поддержку уведомлений при инициализации (безопасная для iOS)
  useEffect(() => {
    const checkSupport = () => {
      try {
        const supported = 'Notification' in window;
        setIsSupported(supported);
        
        if (supported) {
          setPermission(Notification.permission as NotificationPermission);
        }
      } catch (error) {
        console.warn('Failed to check notification support:', error);
        setIsSupported(false);
        setPermission('denied');
      }
    };

    // Задержка для iOS, чтобы избежать проблем при загрузке
    const timer = setTimeout(checkSupport, CONFIG.timeouts.notificationCheck);
    
    return () => clearTimeout(timer);
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      setError('Уведомления не поддерживаются в этом браузере');
      return 'denied';
    }

    setIsLoading(true);
    setError(null);

    try {
      // Проверяем, что Notification API доступен
      if (typeof Notification === 'undefined') {
        throw new Error('Notification API not available');
      }
      
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result as NotificationPermission;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка при запросе разрешения';
      setError(errorMessage);
      console.warn('Notification permission request failed:', err);
      return 'denied';
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  return {
    permission,
    isSupported,
    requestPermission,
    isLoading,
    error
  };
}; 